import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addLicense, rebuildRevision } from '../../../redux/globalLicenseCorrections/actions';
import * as GlobalLicenseCorrectionsStore from '../../../redux/globalLicenseCorrections';
import { BUTTON_SIZE, BUTTON_TYPE, LoaderButton } from '../../generic';
import css from './BottomActions.module.less';
import { LoadingBoundary } from '../../generic/LoadingBoundary';
import Button from '../../generic/Button';
import type { NewLicense } from '../../modals/NewModalAddLicense';
import NewModalAddLicense from '../../modals/NewModalAddLicense';

/**
 * Buttons for the left side of the bottom actions.
 * Contains the add-license button.
 */
const BottomActionsLeft = () => {
  const dispatch = useDispatch();
  const [isModalVisible, setModalVisible] = React.useState(false);

  const onAddLicense = (newLicense: NewLicense) => {
    dispatch(addLicense(newLicense));
    setModalVisible(false);
  };

  return (
    <div>
      <Button
        onClick={() => setModalVisible(true)}
        type={BUTTON_TYPE.PRIMARY}
        size={BUTTON_SIZE.LARGE}>
        Add License
      </Button>
      {
        isModalVisible &&
          <NewModalAddLicense
            onSave={onAddLicense}
            onCancel={() => setModalVisible(false)}
            isNested={false}
            shouldSkipWarning />
      }
    </div>);
};

/**
 * Buttons for the right side of the bottom actions.
 * Contains the rebuild button and task info.
 */
const BottomActionsRight = () => {
  const scheduledTaskValue = useSelector(GlobalLicenseCorrectionsStore.selectors.scheduledTaskSelector);

  return (
    <LoadingBoundary
      asyncValue={scheduledTaskValue}
      renderValue={({ value: scheduledTask }) =>
        <div className={css.bottomActions}>
          <span>Rebuild scheduled: Task #{scheduledTask.id}</span>
          <RebuildButton />
        </div>}
      renderEmpty={(
        <div className={css.bottomActions}>
          <RebuildButton />
        </div>)}
      renderError={({ errors }) => <span>error: ${errors.join('. ')}</span>}
      renderLoading={(
        <div className={css.bottomActions}>
          <RebuildButton isLoading isDisabled />
        </div>)}
    />
  );
};

type RebuildButtonProps = {
  isLoading?: boolean;
  isDisabled?: boolean;
};

const RebuildButton: React.FC<RebuildButtonProps> = ({ isLoading, isDisabled }) => {
  const dispatch = useDispatch();

  return (
    <LoaderButton
      type={BUTTON_TYPE.PRIMARY}
      size={BUTTON_SIZE.LARGE}
      onClick={() => dispatch(rebuildRevision())}
      isLoading={isLoading}
      isDisabled={isDisabled}
    >
      Rebuild
    </LoaderButton>);
};

/** Action buttons for the bottom of the License Corrections Card. */
export const BottomActions = () =>
  <div className={css.bottomActions}>
    <BottomActionsLeft />
    <BottomActionsRight />
  </div>;
